import React, { useEffect, useState } from "react";
import "./CSS/UserManagement.css";
import { backend_url } from "../App";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { isAdmin, isSuperAdmin } from "../utils/roleUtils";
import { MENU_ACCESS, defaultPermissionsFor, isFullAccessRole } from "../utils/menuAccess";

const MENU_GROUPS = ["Retail", "Boutique"];

const ROLES = [
  { value: "super-admin", label: "Super Admin", color: "#7c3aed" },
  { value: "admin",       label: "Admin",       color: "#dc2626" },
  { value: "admin-staff", label: "Admin Staff",  color: "#2563eb" },
  { value: "staff",       label: "Staff",        color: "#059669" },
  { value: "stock-staff", label: "Stock Staff",  color: "#d97706" },
];

const roleLabel = (r) => ROLES.find((x) => x.value === r)?.label || r;
const roleClass = (r) => ({
  "super-admin":"role-super-admin",
  admin:        "role-admin",
  "admin-staff":"role-admin-staff",
  staff:        "role-staff",
  "stock-staff":"role-stock-staff",
})[r] || "";

const avatarColor = (r) => ({
  "super-admin":"#7c3aed",
  admin:        "#dc2626",
  "admin-staff":"#2563eb",
  staff:        "#059669",
  "stock-staff":"#d97706",
})[r] || "#7c3aed";

const initForm = {
  name: "", username: "", email: "",
  password: "", role: "staff", isActive: true,
  permissions: defaultPermissionsFor("staff"),
};

const UserManagement = () => {
  const [users, setUsers]         = useState([]);
  const [search, setSearch]       = useState("");
  const [modal, setModal]         = useState(false);
  const [editUser, setEditUser]   = useState(null);
  const [form, setForm]           = useState(initForm);
  const [saving, setSaving]       = useState(false);
  const [delConfirm, setDelConfirm] = useState(null);
  const [roleAccessOpen, setRoleAccessOpen] = useState(false);

  const token   = sessionStorage.getItem("adminToken");
  const myId    = (() => {
    try { return JSON.parse(atob(token.split(".")[1])).id; } catch { return null; }
  })();

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res  = await fetch(`${backend_url}/api/admin/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      // Super admin accounts are shown only to a super admin
      if (data.success) setUsers(isSuperAdmin() ? data.users : data.users.filter((u) => u.role !== "super-admin"));
      else toast.error(data.message || "Failed to load users");
    } catch { toast.error("Failed to load users"); }
  };

  const filtered = users.filter((u) =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const counts = {
    total:      users.length,
    admin:      users.filter((u) => u.role === "admin").length,
    adminStaff: users.filter((u) => u.role === "admin-staff").length,
    staff:      users.filter((u) => u.role === "staff").length,
    stockStaff: users.filter((u) => u.role === "stock-staff").length,
  };

  const openAdd = () => {
    setEditUser(null);
    setForm(initForm);
    setModal(true);
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({
      name: user.name, username: user.username,
      email: user.email, password: "",
      role: user.role, isActive: user.isActive,
      permissions: Array.isArray(user.permissions) ? user.permissions : defaultPermissionsFor(user.role),
    });
    setModal(true);
  };

  const closeModal = () => { setModal(false); setEditUser(null); setForm(initForm); };

  const handleSave = async () => {
    if (!form.name || !form.username || !form.email || (!editUser && !form.password)) {
      toast.error("Please fill all required fields");
      return;
    }
    setSaving(true);
    try {
      const url    = editUser
        ? `${backend_url}/api/admin/staff/${editUser._id}`
        : `${backend_url}/api/admin/staff`;
      const method = editUser ? "PUT" : "POST";

      const body = { ...form };
      if (editUser && !body.password) delete body.password;
      // Admin / Super Admin always get every menu — no custom list stored
      if (isFullAccessRole(body.role)) body.permissions = null;

      const res  = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        fetchUsers();
        closeModal();
      } else {
        toast.error(data.message || "Failed to save");
      }
    } catch { toast.error("An error occurred"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      const res  = await fetch(`${backend_url}/api/admin/staff/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        setUsers((prev) => prev.filter((u) => u._id !== id));
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch { toast.error("An error occurred"); }
    finally { setDelConfirm(null); }
  };

  return (
    <>
      <div className="um-page">
        {/* Banner */}
        <div className="um-banner">
          <div className="um-title-row">
            <h1 className="um-title">User Management</h1>
            <span className="um-badge">{counts.total} users</span>
            <button className="um-access-trigger" onClick={() => setRoleAccessOpen(true)}>
              <span className="um-access-trigger-icon">📋</span>
              Role Access
            </button>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="um-stats">
          <div className="um-stat-card um-sc-total">
            <div className="um-stat-icon">👥</div>
            <div className="um-stat-info">
              <span className="um-stat-label">Total</span>
              <span className="um-stat-value">{counts.total}</span>
            </div>
          </div>
          <div className="um-stat-card um-sc-admin">
            <div className="um-stat-icon">🛡️</div>
            <div className="um-stat-info">
              <span className="um-stat-label">Admin</span>
              <span className="um-stat-value">{counts.admin}</span>
            </div>
          </div>
          <div className="um-stat-card um-sc-adminstaff">
            <div className="um-stat-icon">🔧</div>
            <div className="um-stat-info">
              <span className="um-stat-label">Admin Staff</span>
              <span className="um-stat-value">{counts.adminStaff}</span>
            </div>
          </div>
          <div className="um-stat-card um-sc-staff">
            <div className="um-stat-icon">👤</div>
            <div className="um-stat-info">
              <span className="um-stat-label">Staff</span>
              <span className="um-stat-value">{counts.staff}</span>
            </div>
          </div>
          <div className="um-stat-card um-sc-stock">
            <div className="um-stat-icon">📦</div>
            <div className="um-stat-info">
              <span className="um-stat-label">Stock Staff</span>
              <span className="um-stat-value">{counts.stockStaff}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="um-content">
          {/* Controls */}
          <div className="um-controls">
            <div className="um-search-wrap">
              <span>🔍</span>
              <input
                type="text"
                placeholder="Search by name, username or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button className="um-add-btn" onClick={openAdd}>+ Add User</button>
          </div>

          {/* Table */}
          <div className="um-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.map((user, idx) => (
                    <tr key={user._id}>
                      <td style={{ color: "#9ca3af", fontWeight: 600 }}>{idx + 1}</td>
                      <td>
                        <div className="um-user-cell">
                          <div className="um-avatar" style={{ background: avatarColor(user.role) }}>
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="um-user-info">
                            <span className="um-user-name">{user.name}</span>
                            <span className="um-user-email">{user.email}</span>
                          </div>
                        </div>
                      </td>
                      <td><span className="um-username-chip">@{user.username}</span></td>
                      <td>
                        <span className={`um-role-badge ${roleClass(user.role)}`}>
                          {roleLabel(user.role)}
                        </span>
                      </td>
                      <td>
                        <span className="um-status">
                          <span className={`um-status-dot ${user.isActive ? "active" : "inactive"}`} />
                          {user.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={{ color: "#6b7280" }}>
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}
                      </td>
                      <td>
                        {user._id === myId ? (
                          <span className="um-btn-self">You</span>
                        ) : user.role === "super-admin" && !isSuperAdmin() ? (
                          <span className="um-btn-self">Protected</span>
                        ) : (
                          <div className="um-actions">
                            <button className="um-btn-edit" onClick={() => openEdit(user)}>Edit</button>
                            {isAdmin() && <button className="um-btn-delete" onClick={() => setDelConfirm(user)}>Delete</button>}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7">
                      <div className="um-empty">
                        <div className="um-empty-icon">👥</div>
                        No users found
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div className="um-modal-overlay" onClick={closeModal}>
          <div className="um-modal" onClick={(e) => e.stopPropagation()}>
            <div className="um-modal-header">
              <h3>👤 {editUser ? "Edit User" : "Add New User"}</h3>
              <button className="um-modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="um-modal-body">
              <div className="um-form-row">
                <div className="um-form-group">
                  <label>Full Name *</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Ravi Kumar" />
                </div>
                <div className="um-form-group">
                  <label>Username *</label>
                  <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="e.g. ravi_kumar" />
                </div>
              </div>

              <div className="um-form-group">
                <label>Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="e.g. ravi@aavryaa.com" />
              </div>

              <div className="um-form-group">
                <label>Password {editUser ? "(leave blank to keep current)" : "*"}</label>
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editUser ? "Enter new password to change" : "Set password"} />
                {editUser && <span className="um-pass-hint">Leave blank to keep the existing password.</span>}
              </div>

              <div className="um-form-row">
                <div className="um-form-group">
                  <label>Role *</label>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, permissions: defaultPermissionsFor(e.target.value) })}>
                    {ROLES.filter((r) => r.value !== "super-admin" || isSuperAdmin()).map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div className="um-form-group">
                  <label>Status</label>
                  <select value={form.isActive ? "active" : "inactive"} onChange={(e) => setForm({ ...form, isActive: e.target.value === "active" })}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Menu Access — sidebar menus this user can see */}
              <div className="um-form-group">
                <label>Menu Access</label>
                {isFullAccessRole(form.role) ? (
                  <span className="um-pass-hint">
                    {roleLabel(form.role)} has access to all menus, including Users, Settings and Audit Log.
                  </span>
                ) : (
                  <span className="um-pass-hint">Tick the sidebar menus this user can open.</span>
                )}
                {MENU_GROUPS.map((group) => {
                  const items   = MENU_ACCESS.filter((m) => m.group === group);
                  const locked  = isFullAccessRole(form.role);
                  const checked = (key) => locked || form.permissions.includes(key);
                  const allOn   = items.every((m) => checked(m.key));
                  const toggleGroup = () => {
                    const keys = items.map((m) => m.key);
                    setForm({
                      ...form,
                      permissions: allOn
                        ? form.permissions.filter((k) => !keys.includes(k))
                        : [...new Set([...form.permissions, ...keys])],
                    });
                  };
                  const toggle = (key) => setForm({
                    ...form,
                    permissions: form.permissions.includes(key)
                      ? form.permissions.filter((k) => k !== key)
                      : [...form.permissions, key],
                  });
                  return (
                    <div key={group} className="um-perm-group">
                      <label className="um-perm-group-title">
                        <input type="checkbox" checked={allOn} disabled={locked} onChange={toggleGroup} />
                        {group}
                      </label>
                      <div className="um-perm-grid">
                        {items.map((m) => (
                          <label key={m.key} className={`um-perm-item ${locked ? "locked" : ""}`}>
                            <input
                              type="checkbox"
                              checked={checked(m.key)}
                              disabled={locked}
                              onChange={() => toggle(m.key)}
                            />
                            {m.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="um-modal-footer">
              <button className="um-btn-cancel" onClick={closeModal}>Cancel</button>
              <button className="um-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : editUser ? "Update User" : "Create User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {delConfirm && (
        <div className="um-modal-overlay" onClick={() => setDelConfirm(null)}>
          <div className="um-modal" style={{ maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
            <div className="um-modal-header" style={{ background: "linear-gradient(135deg,#dc2626,#b91c1c)" }}>
              <h3>🗑️ Delete User</h3>
              <button className="um-modal-close" onClick={() => setDelConfirm(null)}>×</button>
            </div>
            <div className="um-modal-body">
              <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
                Are you sure you want to delete <strong>{delConfirm.name}</strong> (@{delConfirm.username})?
                This action cannot be undone.
              </p>
            </div>
            <div className="um-modal-footer">
              <button className="um-btn-cancel" onClick={() => setDelConfirm(null)}>Cancel</button>
              <button
                className="um-btn-save"
                style={{ background: "linear-gradient(135deg,#dc2626,#b91c1c)" }}
                onClick={() => handleDelete(delConfirm._id)}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Access Popup */}
      {roleAccessOpen && (
        <div className="um-modal-overlay" onClick={() => setRoleAccessOpen(false)}>
          <div className="um-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="um-modal-header">
              <h3>📋 Role Access</h3>
              <button className="um-modal-close" onClick={() => setRoleAccessOpen(false)}>×</button>
            </div>
            <div className="um-modal-body">
              <ul className="um-access-note-list">
                {isSuperAdmin() && (
                  <li>
                    <span className="um-role-badge role-super-admin">Super Admin</span>
                    <span className="um-access-note-text">All pages + image/video downloads</span>
                  </li>
                )}
                <li>
                  <span className="um-access-note-text">
                    The lists below are each role's default menus. Untick or tick menus per user under <strong>Menu Access</strong> when adding or editing a user.
                  </span>
                </li>
                <li>
                  <span className="um-role-badge role-admin">Admin</span>
                  <span className="um-access-note-text">All pages available</span>
                </li>
                <li>
                  <span className="um-role-badge role-admin-staff">Admin Staff</span>
                  <span className="um-access-note-text">
                    Dashboard, Products, Orders, Customers, Stock, Excel Upload, FAQ, Promocode, Our Ideas, HomePage
                  </span>
                </li>
                <li>
                  <span className="um-role-badge role-staff">Staff</span>
                  <span className="um-access-note-text">Dashboard, Products, Orders</span>
                </li>
                <li>
                  <span className="um-role-badge role-stock-staff">Stock Staff</span>
                  <span className="um-access-note-text">Stock, Excel Upload</span>
                </li>
              </ul>
            </div>
            <div className="um-modal-footer">
              <button className="um-btn-save" onClick={() => setRoleAccessOpen(false)}>Got it</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
};

export default UserManagement;
